/**
 * RequestExtension — student-side "Manage this campaign" panel that
 * renders at the bottom of a campaign detail page. Holds the extension
 * request affordance for now; can grow later to hold other owner
 * actions (edit description, upload documents, close early, etc.).
 *
 * Auth note: this prototype has no real auth yet, so the panel renders
 * for every visitor with a small "In production, only the campaign
 * owner sees this section" caption. When JWT/session auth arrives,
 * this is where we gate on `session.userId === campaign.ownerId`.
 */

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Clock, Info, Send, ShieldCheck, CircleAlert, CalendarClock } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import {
  canRequestExtension,
  requestExtension,
  getPendingExtensionForCampaign,
  getApprovedExtensionForCampaign,
  subscribeExtensions,
} from "../lib/campaignExtensions";
import { getPolicy, subscribePolicy } from "../lib/platformPolicy";

const relDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const fmt$ = (n) => `$${Math.round(n).toLocaleString()}`;

export const RequestExtension = ({ campaignId, studentFirstName, goalAmount, signalOpenRequest }) => {
  const [policy, setPolicy] = useState(getPolicy);
  const [pending, setPending] = useState(() => getPendingExtensionForCampaign(campaignId));
  const [approved, setApproved] = useState(() => getApprovedExtensionForCampaign(campaignId));
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const eligibility = canRequestExtension(campaignId);

  useEffect(() => subscribePolicy(setPolicy), []);
  useEffect(
    () =>
      subscribeExtensions(() => {
        setPending(getPendingExtensionForCampaign(campaignId));
        setApproved(getApprovedExtensionForCampaign(campaignId));
      }),
    [campaignId],
  );

  // Open the dialog when the parent signals from a remote affordance
  // (e.g. the inline "Request N more days" link on the urgency pill).
  // Guard on eligibility so a signal from the pill never opens a
  // dialog for a campaign that can't request one.
  useEffect(() => {
    if (!signalOpenRequest) return;
    if (!canRequestExtension(campaignId).canRequest) return;
    setOpen(true);
  }, [signalOpenRequest, campaignId]);

  // Disbursement window — respects an approved extension so the copy
  // reflows the moment an admin adds days.
  const effectiveDurationDays =
    policy.campaignDurationDays + (approved ? approved.daysAdded || policy.extensionDays : 0);
  const goalNum = Number(goalAmount) || 0;

  const onSubmit = () => {
    const trimmed = reason.trim();
    if (trimmed.length < 20) {
      toast.error("Please tell us why you need the extension.", {
        description: "A short note (at least 20 characters) helps the admin decide quickly.",
      });
      return;
    }
    requestExtension({ campaignId, requesterName: studentFirstName, reason: trimmed });
    toast.success("Extension requested", {
      description: "A My Rich Aunt admin will review your request. You'll see the decision here.",
    });
    setOpen(false);
    setReason("");
  };

  return (
    <>
      <div
        data-testid="manage-campaign-panel"
        className="mt-10 overflow-hidden rounded-lg border border-ink/10 bg-sand"
      >
        <div className="flex items-center justify-between border-b border-ink/10 bg-beige/60 px-5 py-3">
          <div className="flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.22em] text-ink">
            <ShieldCheck className="h-3.5 w-3.5 text-ink/60" strokeWidth={2} />
            Manage this campaign
          </div>
          <span className="text-[13px] italic text-ink/50">
            In production, only the campaign owner sees this.
          </span>
        </div>

        <div className="p-5">
          {/* Weekly disbursement projection — helps students see cadence
              before the first grant lands. Recalculates when a 30-day
              extension is approved and the effective duration grows. */}
          {goalNum > 0 && (
            <div
              data-testid="weekly-disbursement-preview"
              className="mb-6 flex items-start gap-4 rounded-md border border-tangerine/25 bg-tangerine/[0.05] p-4"
            >
              <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-tangerine/15">
                <CalendarClock className="h-4 w-4 text-tangerine" strokeWidth={1.75} />
              </span>
              <div className="flex-1">
                <div className="flex items-baseline justify-between gap-3">
                  <h4 className="text-[13px] font-semibold uppercase tracking-[0.22em] text-tangerine">
                    Weekly disbursements
                  </h4>
                  {approved && (
                    <span className="text-[13px] font-semibold uppercase tracking-[0.18em] text-grass">
                      Recalculated · extension approved
                    </span>
                  )}
                </div>
                <p className="mt-1.5 text-base leading-relaxed text-ink">
                  My Rich Aunt disburses grants in <strong>weekly installments</strong> across your {effectiveDurationDays}-day campaign
                  {approved ? ` (including your ${approved.daysAdded || policy.extensionDays}-day extension)` : ""}.
                </p>
                <p className="mt-1.5 text-[13px] italic text-ink/60">
                  Each week&rsquo;s installment scales to what donors have contributed by that week.
                </p>
              </div>
            </div>
          )}

          <div className="flex items-start gap-4">
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-cobalt/12">
              <Clock className="h-4 w-4 text-cobalt" strokeWidth={1.75} />
            </span>
            <div className="flex-1">
              <h4 className="font-serif text-3xl leading-tight text-ink">
                {approved
                  ? "Extension approved"
                  : pending
                    ? "Extension request under review"
                    : `Need another ${policy.extensionDays} days?`}
              </h4>
              <p className="mt-1 text-base leading-relaxed text-olive">
                {approved
                  ? `Your ${approved.daysAdded}-day extension was approved on ${relDate(approved.decidedAt)}. Your campaign end date has been updated.`
                  : pending
                    ? `You requested an extension on ${relDate(pending.requestedAt)}. A My Rich Aunt admin will review it shortly.`
                    : `Every campaign is allowed one ${policy.extensionDays}-day extension with admin approval. Tell us why you need it and we'll take a look.`}
              </p>

              {/* State-specific control */}
              {!approved && !pending && (
                <button
                  type="button"
                  onClick={() => setOpen(true)}
                  disabled={!eligibility.canRequest}
                  data-testid="extension-request-btn"
                  className={`mt-4 inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[13px] font-semibold transition ${ eligibility.canRequest ? "bg-ink text-sand hover:bg-cobalt" : "cursor-not-allowed bg-ink/10 text-ink/40" }`}
                >
                  <Clock className="h-3.5 w-3.5" strokeWidth={2} /> Request {policy.extensionDays}-day extension
                </button>
              )}

              {!approved && !eligibility.canRequest && !pending && (
                <p className="mt-3 flex items-start gap-1.5 text-[13px] italic text-ink/60">
                  <CircleAlert className="mt-0.5 h-3 w-3" strokeWidth={2} />
                  {eligibility.reason}
                </p>
              )}

              {pending && pending.reason && (
                <p className="mt-3 rounded-md border border-ink/10 bg-beige/60 p-3 text-[13px] italic text-ink/70">
                  <span className="not-italic font-medium text-ink">Your reason: </span>
                  {pending.reason}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Request dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="extension-request-dialog">
          <DialogTitle className="font-serif text-3xl leading-tight text-ink">
            Request a {policy.extensionDays}-day extension
          </DialogTitle>
          <DialogDescription className="mt-1 text-[13px] text-olive">
            Every campaign is allowed one {policy.extensionDays}-day extension with admin approval. Tell us briefly why you need it.
          </DialogDescription>

          <div className="mt-5">
            <label className="block text-[13px] font-semibold uppercase tracking-[0.22em] text-ink/60">
              Reason
            </label>
            <Textarea
              rows={5}
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Example: My interview was rescheduled and I still need funds for interview attire."
              className="mt-2"
              data-testid="extension-reason-input"
            />
            <div className="mt-2 flex items-center justify-between text-[13px] text-olive">
              <span className="flex items-center gap-1">
                <Info className="h-3 w-3" strokeWidth={2} />
                Reviewed by a My Rich Aunt admin, typically within one business day.
              </span>
              <span>{500 - reason.length} left</span>
            </div>
          </div>

          <div className="mt-5 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-full px-3 py-1.5 text-[13px] font-medium text-olive hover:text-ink"
              data-testid="extension-cancel-btn"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onSubmit}
              className="inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-1.5 text-[13px] font-semibold text-sand hover:bg-cobalt"
              data-testid="extension-submit-btn"
            >
              <Send className="h-3.5 w-3.5" strokeWidth={2} /> Submit request
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
